<template>
  <div v-if="loaded">
    <!-- Row top buttons -->
    <div class="row q-mb-lg">
      <div class="col-6" style="justify-content: flex-start">
        <q-btn
          class="capitalize ctrl-button"
          color="grey-5"
          :label="`${$store.state.stringsStore.strings.action.toOverview}`"
          @click="$router.push(`/${module}`)"
          icon="keyboard_backspace"
        />
      </div>
      <div class="col-6">
        <q-btn
          style="float: right"
          class="capitalize ctrl-button right q-ml-md"
          :color="readOnly ? 'grey-5' : 'ctrl-turquoise'"
          :label="
            readOnly
              ? $store.state.stringsStore.strings.action.edit
              : $store.state.stringsStore.strings.action.save
          "
          @click="save()"
          :icon-right="readOnly ? 'edit' : 'save'"
        />
        <q-toggle
          style="float: right"
          class="q-pt-sm"
          v-model="data.sending_enabled"
          :disable="readOnly"
          color="secondary"
          left-label
          false-value="0"
          true-value="1"
          label="Activated"
        />
      </div>
    </div>

    <!-- Columns -->
    <div class="row gutter-sm q-mb-lg">
      <!-- Left -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="row q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{ getString(`module.name.emails`) }}:
              {{ getString(`module.emails.code.${data.code}`) }}
            </p>

            <div class="card-block">
              <div class="row">
                <div class="col-8">
                  <q-field
                    :error="!isEmailValid"
                    error-label="eg: test@company.nl,test@gmail.com"
                  >
                    <q-input
                      :disable="!readOnly"
                      type="email"
                      class="full-width q-mb-md"
                      float-label="Test e-mail"
                      v-model="recipients"
                      :error="!isEmailValid"
                    />
                  </q-field>
                </div>

                <div class="col-4">
                  <q-btn
                    style="margin-top: 10px"
                    :disable="!readOnly"
                    :loading="!mailSent"
                    color="ctrl-turquoise"
                    class="capitalize item-center q-ml-md"
                    icon-right="mail"
                    :label="
                      $store.state.stringsStore.language === 'en'
                        ? 'Send test email'
                        : 'Stuur testemail'
                    "
                    @click="sendEmail"
                  />
                </div>
              </div>
              <div class="row">
                <q-field
                  class="full-width q-mb-md"
                  :error="!isBccValid"
                  error-label="eg: test@company.nl,test@gmail.com"
                >
                  <q-input
                    :disable="readOnly"
                    type="text"
                    class="full-width"
                    float-label="BCC"
                    :error="!isBccValid"
                    v-model="data.bcc"
                  />
                </q-field>
              </div>
              <div class="row">
                <div class="q-if-label q-if-label-above">
                  <div class="q-if-label-inner ellipsis">
                    {{
                      $store.state.stringsStore.language === 'en'
                        ? 'Subject'
                        : 'Onderwerp'
                    }}
                  </div>
                </div>
                <br />
                <q-editor
                  :disable="readOnly"
                  ref="subject"
                  class="full-width q-mb-md"
                  v-model="subject"
                  min-height="4rem"
                  max-height="4rem"
                  @keydown.native.enter.prevent
                  @keyup.native="saveRange('subject')"
                  @click.native="saveRange('subject')"
                  @input="editorUpdate('subject')"
                  :toolbar="[]"
                />
              </div>
              <div class="row">

                <q-field
                    class="full-width q-mb-md"
                >
                    <q-search
                        v-model="data.category.name"
                        clearable
                        :disable="
                            readOnly
                        "
                        :float-label="getString(null, 'Category', 'Categorie')"
                    >
                        <q-autocomplete
                            value-field="name"
                            :max-results="25"
                            :min-characters="2"
                            @search="
                                autocompleteCategory
                            "
                            @selected="selectCategory"
                        />
                    </q-search>
                </q-field>
              </div>
              <div class="row">
                <div class="q-if-label q-if-label-above">
                  <div class="q-if-label-inner ellipsis">Content</div>
                </div>
                <br />
                <q-editor
                  class="full-width q-mb-md"
                  :disable="readOnly"
                  ref="content"
                  v-model="content"
                  @keyup.native="saveRange('content')"
                  @click.native="saveRange('content')"
                  @fullscreen="addVariablesOnToolbar"
                  @keydown.native.enter.prevent="addLine"
                  @input="editorUpdate('content')"
                  :toolbar="toolbar"
                >
                  <template v-slot:variables>
                    <q-btn-dropdown
                      dense
                      no-caps
                      ref="variables"
                      no-wrap
                      unelevated
                      label="Variables"
                      size="m"
                    >
                      <q-list link>
                        <q-item
                          tag="label"
                          v-for="variable in variables"
                          :key="variable"
                          clickable
                          @click.native="addVariable(variable)"
                        >
                          <q-item-main>{{ variable }}</q-item-main>
                        </q-item>
                      </q-list>
                    </q-btn-dropdown>
                  </template>
                </q-editor>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right -->
      <div class="col-md-6 col-sm-12 col-xs-12 q-pa-sm">
        <div class="row">
          <div class="full-width">
            <p class="card-title">{{ getString('module.emails.attachments.title') }} (Max &#60; 10 MB)</p>
            <div class="card-block q-mb-md">
              <table class="q-mb-md">
                <tr>
                  <th>{{ getString('module.emails.attachments.filename') }}</th>
                  <th>{{ getString('module.emails.attachments.original-file') }}</th>
                  <th>
                    {{ attachmentSizeColumn }}
                  </th>
                  <th></th>
                </tr>
                <p class="q-mt-md" v-show="!attachments.length">
                  {{ getString('module.emails.attachments.no-attachments') }}
                </p>
                <template v-if="attachmentsLoaded">
                  <tr
                    v-for="(attachment, id) in attachments"
                    :key="attachment.name"
                  >
                    <td>
                        <q-input
                          :disable="readOnly"
                          type="text"
                          :error="attachment.filenameError"
                          v-model="attachment.filename"
                        />
                    </td>
                    <td>
                      <a :href="attachment.url" target="_blank">{{
                        attachment.originalName
                      }}</a>
                    </td>
                    <td>{{ readableBytes(attachment.size) }}</td>
                    <td class="attachment-remove" @click="!readOnly && removeAttachment(id)">
                      <q-icon :disabled="readOnly" size="large" name="delete" />
                    </td>
                  </tr>
                </template>
              </table>
              <q-btn
                class="capitalize ctrl-button"
                :color="readOnly ? 'grey-5' : 'primary'"
                :disabled="readOnly"
                :label="getString('module.emails.attachments.button-add')"
                @click="isAttachmentModalOpen = true"
              />
            </div>
          </div>
        </div>
        <div class="row sticky q-mb-sm">
          <div class="full-width">
            <p class="card-title">
              {{
                $store.state.stringsStore.language === 'en'
                  ? 'Variables'
                  : 'Variabelen'
              }}
              <q-list link>
                <div v-if="data.variables">
                  <variable-tag
                    v-for="variable in variables"
                    :disable="readOnly"
                    :key="variable"
                    :name="variable"
                    :onlyReadable="true"
                    :customText="customText(variable)"
                    @click.native="addVariable(variable)"
                    @onEditText="editCustomVariable"
                  >
                  </variable-tag>
                </div>
              </q-list>
            </p>
          </div>
        </div>
      </div>
    </div>
    <!-- image select modal -->
    <q-modal v-if="isAttachmentModalOpen" v-model="isAttachmentModalOpen">
      <file-select-modal :guid="guid" />
    </q-modal>
  </div>
</template>

<script>
import Vue from 'vue';

// Mixins
import getString from '@/helpers/GetStrings';
import suggestUrl from '@/mixins/suggestUrl';
import saveChanges from '@/mixins/detailViews/saveChanges';
import { required, email } from 'vuelidate/lib/validators';
import FileSelectModal from '@/components/modal/FileSelectModal.vue';
import createUuid from '@/helpers/createUuid';
import EventBus from '@/event-bus';
import common from '@/mixins/library/common';
import VariableTag from '@/components/VariableTag.vue';

export default {
  mixins: [suggestUrl, saveChanges, common],
  components: {
    VariableTag,
    FileSelectModal,
  },
  data() {
    return {
      categoriesLoaded: false,
      guid: createUuid(),
      isAttachmentModalOpen: false,
      attachments: [],
      attachmentsLoaded: false,
      loaded: false,
      readOnly: true,
      containsErrors: true,
      module: 'emails',
      endpoint: 'emails',
      inputSelected: '',
      recipients: '',
      mailSent: true,
      isEmailValid: true,
      currentRange: {},
      toolbar: [
        [
          'bold',
          'italic',
          'strike',
          'underline',
          'removeFormat',
          'unordered',
          'ordered',
        ],
        ['link'],
        ['undo', 'redo'],
        [
          {
            label: this.$q.i18n.editor.formatting,
            icon: this.$q.icon.editor.formatting,
            list: 'no-icons',
            options: ['p', 'h6', 'h5', 'h4', 'h3', 'h2'],
          },
        ],
        ['fullscreen'],
      ],
      data: {
        id: null,
        subject: '',
        content: '',
        variables: null,
        custom_variables: null,
        code: '',
        bcc: '',
        sending_enabled: 0,
      },
      categories: [],
      content: '',
      subject: '',
      labelSet: false,
    };
  },
  validations: {
    email: { required, email },
  },
  methods: {

    /**
     * Search for category
     */
    autocompleteCategory(terms, done) {
      // Reset location on input
      this.axios.get(`emails/category?autocomplete=${terms}`).then((response) => {
        // Add new item
        const found = response.data.email_categories || [];
        const suggestions = found.map(item => ({
          label: item.name,
          id: item.id,
          value: item.id,
        }));
        // allow adding when found does not include the exact term
        if (!suggestions.map(x => x.label.toLowerCase()).find(x => x === terms.toLowerCase())) {
          suggestions.push({
            label: getString(null, `Add category "${terms}?"`, `Add category "${terms}?"`),
            id: 'new',
            value: terms
          });
        }
        done(suggestions);
      });
    },

    selectCategory(found, keyboard) {
      // Prevent adding on keyboard arrow down
      if (keyboard) return;

      if (found.id === 'new') {
        this.axios.post('emails/category', {
          name: found.value
        }).then((res) => {
          if (res.headers.location) {
            const id = res.headers.location.split('/').pop();
            this.data.category.name = found.value;
            this.data.category.id = id;
          }
        });
      }
      this.data.category.name = found.label;
      this.data.category.id = found.id;
    },

    addLine() {
      const edit = this.$refs[this.inputSelected];
      edit.runCmd('insertParagraph');
    },
    addVariablesOnToolbar(state) {
      state ? this.toolbar.push(['variables']) : this.toolbar.pop();
    },
    editorUpdate(field) {
      const toClear = field === 'subject' ? 'br,span' : 'span';
      this.$refs[field]
        .getContentEl()
        .querySelectorAll(toClear)
        .forEach((el) => {
          if (!el.textContent.trim()) {
            el.parentNode.removeChild(el);
          }
        });
      let text = this.$refs[field].getContentEl().innerHTML;
      const htmlEl = document.createElement('div');
      htmlEl.innerHTML = text.trim();
      this.variables.forEach((name) => {
        const varComponent = htmlEl.getElementsByClassName(
          `variables__${name.toLowerCase().split(' ').join('-')}`,
        );
        if (varComponent.length) {
          text = text.replaceAll(varComponent[0].outerHTML, `%${name}%`);
        }
      });
      this.data[field] = text.trim().replace(/&nbsp;/g, ' ');
    },
    getString,
    addVariable(name) {
      if (!this.inputSelected) return;
      const edit = this.$refs[this.inputSelected];
      edit.caret.restore(this.currentRange[this.inputSelected]);
      edit.runCmd('insertText', '%to__replace%');
      const data = edit
        .getContentEl()
        .innerHTML.replaceAll('%to__replace%', this.createVariableTag(name));
      const scrollTop = document.getElementsByTagName('main')[0].scrollTop;
      edit.runCmd('selectAll');
      edit.runCmd('delete');
      edit.runCmd('insertHTML', data);
      document.getElementsByTagName('main')[0].scrollTop = scrollTop;
      edit.focus();
    },
    saveRange(inputSelected) {
      this.inputSelected = inputSelected;
      const edit = this.$refs[this.inputSelected];
      this.currentRange[this.inputSelected] = edit.caret.range;
    },
    createVariableTag(name) {
      const VariableTagComponent = Vue.extend(VariableTag);
      const tag = new VariableTagComponent({ propsData: { name } });
      tag.$mount();
      return tag.$el.outerHTML;
    },
    requestParams() {
      const editors = ['content', 'subject'];
      editors.forEach(n => this.editorUpdate(n));
      this.data.category_id = this.data.category.id;
      return this.data;
    },
    save() {
      if (this.readOnly) {
        this.readOnly = false;
        return;
      }
      if (this.attachments.some((a) => {
        a.filenameError = a.filename.length <= 0;
        return a.filenameError;
      })) {
        this.notifyFormError();
        return;
      }
      this.data.attachments = JSON.stringify(this.formattedAttachments);
      this.saveChanges();
      this.readOnly = !this.readOnly;
    },
    sendEmail() {
      const recipients = this.getEmails(this.recipients);
      if (!recipients) {
        this.isEmailValid = false;
        return;
      }
      this.isEmailValid = true;
      this.mailSent = false;
      this.axios
        .get(`${this.endpoint}/test`, {
          params: { emails: recipients, code: this.data.code },
        })
        .then((res) => {
          if (res.status === 200) {
            this.mailSent = true;
          }
        })
        .catch((error) => {
          this.handleError(error);
          this.notify();
          this.mailSent = true;
        });
    },
    getEmails($emails) {
      if (!$emails) return null;
      const recipients = $emails.split(',');
      return recipients.every((e) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(e).toLowerCase().trim());
      })
        ? recipients
        : null;
    },
    customText(name) {
      const variables = this.customVariables;
      const key = Object.keys(variables).find(k => k.includes(name));
      const toReturn = {};
      toReturn[key] = variables[key];
      return key ? toReturn : undefined;
    },
    editCustomVariable(key, value) {
      const variables = this.customVariables;
      variables[key] = value;
      this.data.custom_variables = JSON.stringify(variables);
    },
    async loadAttachments() {
      await Promise.all(
        this.attachments.map(async (attachment) => {
          let detail = await this.fetchFileDetails(attachment.api_location);
          detail = detail.data.file;
          attachment.id = detail.id;
          attachment.originalName = detail.name;
          attachment.size = detail.size;
          attachment.url = this.getUrlAttachment(detail);
          return attachment;
        }),
      );
      this.attachmentsLoaded = true;
    },
    getUrlAttachment(detail) {
      return `${process.env.VUE_APP_SITE}${detail.folder.location.replace(
        'api/',
        '',
      )}id/${detail.id}/${detail.name}`;
    },
    removeAttachment(index) {
      this.$delete(this.attachments, index);
    },
  },
  computed: {
    variables() {
      return this.data.variables ? JSON.parse(this.data.variables) : [];
    },
    customVariables() {
      return this.data.custom_variables
        ? JSON.parse(this.data.custom_variables)
        : [];
    },
    isBccValid() {
      return !this.data.bcc || this.getEmails(this.data.bcc) !== null;
    },
    totalSizeAttachments() {
      return this.attachments.length
        ? this.attachments
          .map(a => a.size)
          .reduce((prev, next) => prev + next)
        : 0;
    },
    formattedAttachments() {
      return this.attachments.length
        ? this.attachments.map(a => ({
          lfi_id: a.id,
          filename: a.filename,
          api_location: a.api_location,
          size: a.size,
          filenameError: false,
        }))
        : 0;
    },
    attachmentSizeColumn() {
      const basedString = this.getString('module.emails.attachments.size');
      return this.totalSizeAttachments > 0
        ? basedString.replace('%total%', this.readableBytes(this.totalSizeAttachments))
        : basedString.substring(0, basedString.indexOf('('));
    },
  },
  destroyed() {
    this.$store.commit('setReloadEmail', true);
  },
  async mounted() {
    if (this.$store.getters.reloadEmail) {
      this.$router.go();
    }
    this.$store.commit('setReloadEmail', false);

    // Load the categories
    await this.axios.get('emails/category?autocomplete=').then((res) => {
      this.categories = res.data.email_categories;
    }).finally(() => this.categoriesLoaded = true);

    if (this.$route.params.id) {
      this.axios
        .get(`${this.endpoint}/${this.$route.params.id}`)
        .then((res) => {
          this.data = res.data[this.endpoint];
          // Match the category
          this.data.category = this.categories.find(x => this.data.category_id === x.id);
          let content = this.data.content;
          let subject = this.data.subject;
          Array.from(this.variables).forEach((v) => {
            content = content.replaceAll(`%${v}%`, this.createVariableTag(v));
            subject = subject.replaceAll(`%${v}%`, this.createVariableTag(v));
          });
          this.content = content;
          this.subject = subject;
          this.attachments = this.data.attachments || [];
          this.loaded = true;
          this.loadAttachments();
        });
    } else {
      this.readOnly = false;
    }

    EventBus.$on('file-select-close', () => {
      this.isAttachmentModalOpen = false;
    });

    EventBus.$on(`file-select:${this.guid}`, (data) => {
      if (data !== undefined) {
        // The maximum size of the files is 10MB
        const maxSizeAuth = 1048576 * 10;
        const totalSize = this.totalSizeAttachments + data.size;
        if (totalSize > maxSizeAuth) {
          this.isAttachmentModalOpen = false;
          const msg = this.getString('module.emails.attachments.max-size-error-message')
            .replace('%max-size%', this.readableBytes(maxSizeAuth))
            .replace('%current-size%', this.readableBytes(totalSize));
          this.notifyError(msg);
          return;
        }

        this.attachments.push({
          originalName: data.name,
          id: data.id,
          url: this.getUrlAttachment(data),
          filename: data.name.replace(`.${data.ext}`, ''),
          filenameError: false,
          api_location: data.location.replace('api/library/', ''),
          size: data.size,
        });
        this.isAttachmentModalOpen = false;
      }
    });
  },
};
</script>

<style lang="stylus" scoped>
.sticky {
  position: sticky;
  top: 70px;
}

td, th {
  border: 1px solid #dddddd;
  text-align: left;
  padding: 8px;
}

.attachment-remove {
  text-align: center;
  cursor: pointer;
}
</style>
